import type {
  AbsenceType as AdminAbsenceType,
  AbsenceTypeInput as AdminAbsenceTypeInput,
  CalendarDayType as AdminCalendarDayType,
  OnCallMultiplier as AdminOnCallMultiplier,
  BenefitBuyerType as AdminBenefitBuyerType,
  AdditionalPeopleNumber as AdminAdditionalPeopleNumber,
  BenefitGroupType as AdminBenefitGroupType,
  BonusType as AdminBonusType,
  VatNotAppliedReason as AdminVatNotAppliedReason,
  HolidayCalendarId as AdminHolidayCalendarId,
  EmploymentContractType as AdminEmploymentContractType,
  B2BContractType as AdminB2BContractType,
  CompanyRole as AdminCompanyRole,
  ContractStatus as AdminContractStatus,
  Language as AdminLanguage,
  ProgressionLevel as AdminProgressionLevel,
  Quarter as AdminQuarter,
  Settlement as AdminSettlement,
} from '../../schema/admin';
import type {
  AbsenceType as UserAbsenceType,
  AbsenceTypeInput as UserAbsenceTypeInput,
  CalendarDayType as UserCalendarDayType,
  OnCallMultiplier as UserOnCallMultiplier,
  BenefitGroupType as UserBenefitGroupType,
  VatNotAppliedReason as UserVatNotAppliedReason,
  HolidayCalendarId as UserHolidayCalendarId,
  EmploymentContractType as UserEmploymentContractType,
  CompanyRole as UserCompanyRole,
  BonusType as UserBonusType,
  ContractStatus as UserContractStatus,
  Language as UserLanguage,
  ProgressionLevel as UserProgressionLevel,
  Quarter as UserQuarter,
  Settlement as UserSettlement,
} from '../../schema/user';

import type { CellState } from '../additionalTypes';
import type { ObjectValues } from '../object';

export type InterpolatedVariables<T extends string, U = undefined> =
  T extends `${infer A}{{${infer B}}}${infer C}`
    ? InterpolatedVariables<A, Record<B, number | string>>
    & InterpolatedVariables<C, Record<B, number | string>>
    : U;

export interface SharedEnumMessages {
  calendarDayType: AdminCalendarDayType | UserCalendarDayType;
  absenceType: AdminAbsenceType | UserAbsenceType;
  absenceTypeInput: AdminAbsenceTypeInput | UserAbsenceTypeInput;
  onCallMultiplier: AdminOnCallMultiplier | UserOnCallMultiplier;
  benefitBuyerType: AdminBenefitBuyerType;
  progressionLevel: AdminProgressionLevel | UserProgressionLevel;
  benefitAdditionalPeople: AdminAdditionalPeopleNumber;
  benefitGroupType: AdminBenefitGroupType | UserBenefitGroupType;
  bonusType: AdminBonusType | UserBonusType;
  b2bContractType: AdminB2BContractType;
  language: AdminLanguage | UserLanguage;
  quarter: AdminQuarter | UserQuarter;
  vatNotAppliedReason: AdminVatNotAppliedReason | UserVatNotAppliedReason;
  holidayCalendaryId: AdminHolidayCalendarId | UserHolidayCalendarId;
  companyRole: AdminCompanyRole | UserCompanyRole;
  contractStatus: AdminContractStatus | UserContractStatus;
  employmentContractType: AdminEmploymentContractType | UserEmploymentContractType;
  settlement: AdminSettlement | UserSettlement;
  cellState: CellState;
}

export const enumMessage = <T extends keyof SharedEnumMessages, U extends SharedEnumMessages[T]>(
  enumName: T,
  value: U,
): `${T}-${U}` => `${enumName}-${value}` as const;

export type SharedEnumTranslations = {
  [Key in keyof SharedEnumMessages as `${Key}-${SharedEnumMessages[Key]}`]: string
};

type SharedMessages = typeof sharedMessages[number];
type SharedPluralMessages = keyof (typeof sharedPluralMessages);
export type PluralBuildingBlocksToMessages<T extends Record<string, Record<'other', string>>> = ObjectValues<({
  [key in keyof T]: T[key]['other']
})>;

export type Messages = {
  [Key in keyof SharedEnumTranslations]: undefined
} & {
  [key in PluralBuildingBlocksToMessages<typeof sharedPluralBuildingBlocks>]: InterpolatedVariables<key>
} & {
  [key in SharedMessages]: InterpolatedVariables<key>
} & {
  [key in SharedPluralMessages]: InterpolatedVariables<key>
};

export type MessageKey = keyof Messages;

type PluralSuffixes = 'few' | 'many' | 'one' | 'other' | 'two' | 'zero';
type PluralMessages = Readonly<Record<string, Partial<Record<PluralSuffixes, string>>>>;
type PluralMessagesBuildingBlocks = Readonly<Record<string, Partial<Record<Exclude<PluralSuffixes, 'other'>, string>> & Record<'other', string>>>;

export const makePluralMessages = <T extends PluralMessages>(arg: T): T => arg;
export const parsePluralMessages = <T extends PluralMessages>(messages: T): Record<keyof T, string> => {
  const parsed = Object.entries(messages)
    .reduce<Record<string, string>>((acc, [key, pluralVersions]) => {
      Object.entries({ other: key, ...pluralVersions }).forEach(([pluralVersionKey, message]) => {
        acc[`${key}_${pluralVersionKey}`] = message;
      });

      return acc;
    }, {});

  return parsed as Record<keyof T, string>;
};

export const parsePluralMessageBlocks = <T extends PluralMessagesBuildingBlocks>(
  arg: T,
): Record<PluralBuildingBlocksToMessages<T>, string> => parsePluralMessages(arg);

// Object.fromEntries is typed as a string index signature. The assertion restores
// the literal keys the catalogues need for DefaultLanguage.
export const messagesToIdentityMap = <T extends string>(
  entries: readonly T[],
): { [Key in T]: Key } => Object.fromEntries(
  entries.map((entry) => [entry, entry]),
) as { [Key in T]: Key };

export const sharedPluralBuildingBlocks = makePluralMessages({
  days: {
    one: '{{count}} day',
    other: '{{count}} days',
  },
} as const);

const insertBuildingBlock = <Block extends keyof typeof sharedPluralBuildingBlocks, Name extends string>(
  block: Block,
  variableName: Name,
): `$t(${Block}, {"count": {{${Name}}} })` => `$t(${block}, {"count": {{${variableName}}} })`;

export const sharedPluralMessages = makePluralMessages({
  '{{count}} working days ({{time}})': {
    one: '{{count}} working day ({{time}})',
  },
  '{{count}} working days included': {
    one: '{{count}} working day included',
  },
  '{{count}} entries will be added': {
    one: '{{count}} entry will be added',
  },
  '{{count}} shifts': {
    one: '{{count}} shift',
  },
  '{{count}} day shifts will be added.': {
    one: '{{count}} day shift will be added.',
  },
  '{{count}} night shifts will be added.': {
    one: '{{count}} night shift will be added.',
  },
  '{{count}} entries will be deleted from the time report.': {
    one: '{{count}} entry will be deleted from the time report.',
  },
  'There are no entries in {{count}} working days. Please complete your time report for {{month}} by {{date}}.': {
    one: 'There are no entries in {{count}} working day. Please complete your time report for {{month}} by {{date}}.',
  },
  '{{count}} day shifts': {
    one: '{{count}} day shift',
  },
  '{{count}} night shifts': {
    one: '{{count}} night shift',
  },
  '{{count}} selected days': {
    one: '{{count}} selected day'
  },
  '{{count}} selected entries': {
    one: '{{count}} selected entry'
  },
  '{{validDayShiftsAmount}} of {{count}} day shifts will be added.': {
    one: '{{validDayShiftsAmount}} of {{count}} day shift will be added.',
  },
  '{{validNightShiftsAmount}} of {{count}} night shifts will be added.': {
    one: '{{validNightShiftsAmount}} of {{count}} night shift will be added.',
  },
  '{{count}} months ago': {
    one: '{{count}} month ago',
  },
});

export const sharedMessages = [
  'This field must be exactly {{length}} characters long',
  'This field must be a valid UUID',
  'This field must be lowercase',
  'This field must be uppercase',
  'This field must be at most {{max}} characters long',
  'This field must be at least {{min}} characters long',
  'This field must be a valid URL',
  'This field must not be empty',
  'This field must be selected from provided options',
  'This field must be a valid {{type}}',
  'This field must be a positive number',
  'This field must be a negative number',
  'This field must be an integer',
  'This field must be less than {{less}}',
  'This field must be more than {{more}}',
  'This field must be at most {{max}}',
  'This field must be at least {{min}}',
  'This field must have at most two decimal places',
  'This field must have at most one decimal place',
  'This field must be in a format of {{format}}',
  'This field must be valid',
  'This field must be a valid IBAN',
  'This field must be a valid BIC/SWIFT',
  'This field must be in a format of X/X',
  'E-mail must be valid',
  'This field must have at least {{min}} values selected',
  'Close profile options menu',
  'Open profile options menu',
  'Log in',
  'Continue with Google',
  'Show previous month',
  'Show next month',
  'No search results',
  'Select all',
  'None',
  'Loading',
  'Cancel',
  'Must be a valid ID number',
  'Must be a valid passport number',
  'This is a 404 page, you can\'t hear anything here',
  'Close',
  'Save',
  '{{entityName}} has been added',
  '{{entityName}} has been created',
  '{{entityName}} has been updated',
  '{{entityName}} has been deleted',
  '{{entityName}} has been linked',
  '{{entityName}} has been unlinked',
  '{{entityName}} has been signed',
  '{{entityName}} has been terminated',
  '{{entityName}} has been assigned',
  '{{entityName}} has been discarded',
  '{{entityName}} has been added to favorites',
  '{{entityName}} has been removed from favorites',
  '{{entityName}} has been generated',
  '{{entityName}} has been rejected',
  '{{entityName}} has been approved',
  '{{entityName}} has been regenerated',
  '{{entityName}} failed to add.',
  '{{entityName}} failed to create.',
  '{{entityName}} failed to update.',
  '{{entityName}} failed to delete.',
  '{{entityName}} failed to sign.',
  '{{entityName}} failed to terminate.',
  '{{entityName}} failed to be assigned.',
  '{{entityName}} failed to be discarded.',
  '{{entityName}} failed to be added to favorites.',
  '{{entityName}} failed to be removed from favorites.',
  '{{entityName}} failed to generate.',
  '{{entityName}} failed to regenerate.',
  '{{entityName}} failed to approve.',
  '{{entityName}} failed to link.',
  '{{entityName}} failed to unlink.',
  '{{entityName}} failed to reject.',
  '{{entityName}} has not been added.',
  '{{entityName}} has not been created.',
  '{{entityName}} has not been updated.',
  '{{entityName}} has not been deleted.',
  '{{entityName}} has not been signed.',
  '{{entityName}} has not been terminated.',
  '{{entityName}} has not been assigned.',
  '{{entityName}} has not been discarded.',
  '{{entityName}} has not been added to favorites.',
  '{{entityName}} has not been removed from favorites.',
  '{{entityName}} has not been generated.',
  '{{entityName}} has not been regenerated.',
  '{{entityName}} has not been rejected.',
  '{{entityName}} has not been approved.',
  '{{entityName}} has not been linked.',
  '{{entityName}} has not been unlinked.',
  'No changes have been made to {{entityName}}',
  'The mountains look ominous',
  'There was a problem loading this data.',
  'Please try again later.',
  'Try again',
  'Open in a new tab',
  'Soon',
  'Not available on this date',
  'Reporting is blocked on this date',
  'Project',
  'No matching projects',
  'Worked {{percentage}}% of the expected time on {{date}}',
  'Edit absence entry on {{date}}',
  'Edit work entry for {{name}} on {{date}}',
  'Edit on-call entry for {{name}} on {{date}}',
  'Add an entry for {{date}}',
  'Overtime',
  'No time set',
  'No end time set',
  'Day shift',
  'Night shift',
  'Editing disabled',
  'Hide weekends',
  'Show full weeks',
  'Days column for {{weekday}}',
  'The timesheet for {{date}} is already blocked. If you see any mistakes, contact your Space Owner',
  'Work record',
  'On-call record',
  'Absence record',
  'Add new entry',
  'Edit entry - {{entryType}}',
  'Delete {{absenceType}} added from {{startDate}} to {{endDate}}?',
  'You can\'t undo this action.',
  'Delete entries',
  'Delete',
  'Date',
  'Repeat entry',
  'Until the end of the month ({{date}})',
  'Until the current max reporting date ({{date}})',
  'Select an end date',
  'Repeat until',
  'Additional options',
  'Show / hide additional options',
  'Comment',
  'Enter comment',
  'On-call',
  'days',
  '{{thing}} (optional)',
  '{{thing}} (opt.)',
  'Time',
  'Break',
  'Total',
  'Add the same entry for the following days',
  'Type of shift',
  'Add',
  'Show / hide omit options',
  'Omit weekends',
  'Omit holidays',
  'Omit weekends and public holidays',
  'Activity',
  'Mark as overtime',
  'Report overtime only with the consent of your manager',
  'Absence type',
  'Start date',
  'End date',
  'Calendar for {{date}}',
  'Entry modal',
  'Work',
  'Leave',
  'No project',
  'Allocate cost to',
  'Day shifts are only available on weekends and public holidays',
  'Only night shifts are available on weekdays',
  'Select at least one type of shift to add an entry',
  'Until the end of the week ({{date}})',
  'Until the end of the next week ({{date}})',
  'Weekends will be omitted',
  'Public holidays will be omitted',
  'Shift details',
  'Salary',
  'Schedule',
  'Holiday',
  'Absence',
  'A day shift on the day before a holiday is billed according to the normal schedule, e.g. not billed on weekdays, {{saturdayDayRate}} rate on Saturdays.',
  'Overtime this month',
  'On-call this month',
  'Flextime',
  'We were unable to load shift details',
  'Time balance',
  'Base income',
  'On-call total',
  '{{value}} net',
  '{{value}} gross',
  '{{arg1}} x {{arg2}}',
  'From',
  'To',
  'A day must be selected to create an entry',
  'Cannot report work/leave if non-hourly leave already exists on the same day',
  'Cannot report multiple leave entries on the same day',
  '{{entityName}} already exists',
  '{{entityName}} doesn\'t exist',
  'The position couldn\'t be removed because users are assigned to it',
  'Company type is invalid',
  'Invalid currency',
  'Position is inactive',
  'Can\'t schedule changes in the past',
  'You do not have sufficient permissions',
  'This raise is no longer due — it may have already been applied',
  'The page contains outdated data',
  'Reload',
  'Entry cannot be added because it conflicts with an existing entry',
  'Work cannot be reported during absence',
  'Total work time would exceed 24h',
  'Entries with intersecting work time exist',
  'Days are not included in the current project\'s reporting period',
  'User is no longer assigned to the selected project',
  '{{validAmount}} of {{totalAmount}} entries will be added.',
  '{{reason}} ({{dates}}).',
  '{{reason}} (day shift: {{dates}}).',
  '{{reason}} (night shift: {{dates}}).',
  '{{reason}} (day shift: {{dayShiftDates}}) (night shift: {{nightShiftDates}}).',
  '{{validDayShiftsAmount}} of {{dayShiftsAmount}} and {{validNightShiftsAmount}} of {{nightShiftsAmount}} will be added.',
  '{{validDayShiftsAmount}} of {{dayShiftsAmount}} and {{validNightShiftsAmount}} will be added.',
  '{{validDayShiftsAmount}} and {{validNightShiftsAmount}} of {{nightShiftsAmount}} will be added.',
  '{{dayShiftsAmount}} and {{nightShiftsAmount}} will be added.',
  'No entries will be added.',
  '{{text}} Invalid entries will be omitted.',
  'Entry already exists',
  'The contract no longer has an on-call rate defined',
  'Details',
  'Cannot add entries during leave',
  'Cannot add entries outside the reporting range',
  'Go to time report for {{date}}',
  'YYYY-MM-DD',
  'Toggle calendar',
  'Until {{date}}',
  '+{{time}} break',
  'Total has to be greater than 0',
  'Payout',
  'Refunded',
  'Not refunded',
  'Each extension in the package is an additional, non-refunded cost (e.g. an additional person).',
  'Payroll details',
  'Bonuses',
  'Transfer amount',
  'Tax prepayment',
  'ZUS contribution',
  'PPK amount',
  'There’s nothing here yet - this section will be filled in once the Finance Department has completed the billing process for the past month.',
  'Language learning',
  'Other',
  'Referral',
  'Appreciation',
  'Language',
  'Annual leave',
  'Total bonuses',
  'Delete selected entries',
  'Delete the selected entries?',
  'Back to editing',
  'Edit leave entry',
  'There are no work entries to be deleted',
  'Multiple separate leave entries are selected',
  '+ overtime pay for {{time}}',
  '{{absenceType}} on {{date}}',
  'You cannot add a new entry for today because you have no active contract.',
  'There are no selectable entries in the selected interval',
  'The timesheet for {{yearMonth}} is unavailable as the contract is not active yet this month.',
  'The timesheet for {{yearMonth}} is unavailable due to contract termination on {{terminationDate}}',
  'The timesheet for {{yearMonth}} is blocked for now. It will be unlocked later.',
  'Annual leave overlapping a public holiday will not be deducted from the allowance',
  'Cannot report leave entries on non-working days',
  'The transfer or invoice status does not allow any modifications.',
  'Edit',
  'Effective date {{date}}',
  'Effective date {{date}} ({{description}})',
  'Start month',
  'Conclusion month',
  'Edit benefits in the {{category}} category',
  'Paid',
  'The invoice was marked as correct and the transfer has been executed. You cannot replace the file.',
  'Partially refunded',
  'Fully refunded',
  'Edit note',
  'Add note',
  'Paid price: ',
  'Refunded price: ',
  '{{net}} net ({{gross}} gross, {{vatRate}})',
  'Additional people:',
  '{{number}} x Adult',
  '{{number}} x Kids',
  'Nothing assigned',
  'Click to preview',
  'Invoice status',
  'Not delivered',
  'To be checked',
  'Correct',
  'Incorrect',
  'Invoice delivery type',
  'KSeF',
  'Uploaded via NFS',
  'Both KSeF and NFS',
  'Expected invoice amount',
  'This amount is calculated by the system based on time reports, costs and other data. It is not read from the invoice document itself.',
  'Number',
  'Invoice details',
  'Status',
  'Invoice',
  'Annual leave entitlement',
  'From previous year',
  'On {{date}}, the employee\'s annual leave entitlement will increase to 26 days per year.',
  'On {{date}}, your annual leave entitlement will increase to 26 days per year.',
  'The employee had no signed contract in {{year}}',
  'You had no signed contract in {{year}}',
  'No entry',
  'Missing work or leave entry',
  'Entries',
  'Training package',
  'Work equipment',
  'Business travel',
  'Conference',
  'Cost recharge',
  'Client referral',
  'Sales commissions',
  'Severance pay',
  'Shareholder dividend',
  'Training package external deduction',
  'A negative flextime balance will reduce the payout for this month.',
  'There are no entries on {{days}} {{month}}. Please complete your time report for {{month}} by {{date}}.',
  'Delete entry',
  'Delete {{absenceType}} added for {{date}}?',
  'We have just updated the app',
  'Reload the page to apply these updates. If the problem persists, clear your browser\'s cache.',
  'Your browser is not officially supported',
  'We officially support <chrome/>Chrome and <firefox/>Firefox.',
  'On other browsers, such as Safari, <warn>you may experience visual glitches, crashes, or instability.</warn>',
  'For the best experience, please use Chrome or Firefox.',
  'Click to copy',
  'Copied',
  'The selected activity has no default project assigned.',
  'The selected activity does not allow the work entry to be marked as overtime',
  'The selected activity does not allow the work entry to be repeated',
  'You have {{time}} of {{workTag}} left this year.',
  '{{totalAllowed}} of {{totalRequested}} will be added.',
  'The number of hours you have provided exceeds your {{workTag}} entitlement this year.',
  'Basic pay',
  'The user has no training package hours left. Make sure the Head (SO) has approved this exception.',
  'Use "{{value}}"',
  'The transfer status does not allow any modifications.',
  '{{absenceType}} cannot be reported on days with work entries',
  '{{absenceType}} cannot start or end on a non-working day',
  '{{absenceType}} cannot coexist with leave entries that have already been reported',
  '{{absenceType}} cannot be reported on days with on-call entries',
  '{{absenceType}} cannot consist of only non-working days',
  'Days would exceed the assigned limit',
  'Invoices',
  'Add invoice',
  'Note',
  'Note or comment',
  'Missing invoice file',
  'No invoice for this month',
  'Contact the Finance Department.',
  'Missing invoice number',
  'Invoices for {{period}}',
  'Invoice for {{period}}',
  'Pending',
  'Marked fields are required',
  'The invoice was checked by a Financial Specialist and marked as correct. You cannot replace the file.',
  'The invoice is waiting for approval from a Financial Specialist. You can still replace the file with another version.',
  'The invoice was checked by a Financial Specialist and marked as invalid. You cannot replace the file yourself. A Financial Specialist will contact you.',
  'Invoices list',
  'Drawer with invoices',
  'Invoice entry for {{invoiceName}}',
  'Invoice entry',
  'Current invoices',
  'Invoice for {{date}}',
  'On-call entries cannot be reported on the same days as leave',
  'Summary',
  'Additional pay',
  'Luminis products',
  'Basic part of the package',
  'Extended part of the package',
  'Effective rate',
  `${insertBuildingBlock('days', 'count')}`,
  `You have ${insertBuildingBlock('days', 'days')} of annual leave from previous year to take before {{date}}` as const,
  '{{time}} left in {{year}}' as const,
  '{{time}} used in {{year}}' as const,

  `You have ${insertBuildingBlock('days', 'availableAmount')} of the leave left. This request uses ${insertBuildingBlock('days', 'allowanceTaken')}, leaving you with ${insertBuildingBlock('days', 'allowanceRemaining')} this year.` as const,
  `${insertBuildingBlock('days', 'amountTaken')} will be added` as const,
  `${insertBuildingBlock('days', 'amountTaken')} of ${insertBuildingBlock('days', 'totalAmount')} will be added` as const,
  'You have {{availableAmount}} of the leave left. This request uses {{allowanceTaken}}, leaving you with {{allowanceRemaining}} this year.',
  '{{amountTaken}} will be added',
  '{{amountTaken}} of {{totalAmount}} will be added',
  'Total requires a valid time range',
  'Must be a valid time range',
  'Non-working day',
  'Visible in admin view only',
  'User\'s note',
  'Today',
  'Go to now',
  'Go to {{date}}',
  'Remote work',
  'Whole day',
  'hours',
  'Entry cannot be added because the total work time for this day would exceed 24 h',
  'Can\'t change a multi-day leave into an hourly one',
  'Can\'t change the leave\'s whole-day status while other entries exist',
  'The reporting limit for this absence type has been used up',
  'Exceeds daily leave limit',
  'Exceeds the maximum hours per day',
  'Whole-day leave cannot coexist with work entries',
  'Work cannot be reported together with a whole-day absence record',
  'Entry cannot be added because whole-day leave has already been reported',
  'This change results from an edit made to the variant. It can be removed on the Benefits page.',
  'This benefit is terminated together with the contract.',
  'absence to be made up on another day',
  'holiday, max. 20 or 26 days, 100% paid',
  'if you need more time off, unpaid',
  'birth of a child, wedding, funeral etc., 100% paid',
  'due to health reasons, 80% paid',
  'to care for a sick child under 14 (max. 60 days) or family member (max. 14 days), 80% paid',
  'to care for a child under 14, max. 16 hours or 2 days, 100% paid',
  'to care for a family or household member, max. 5 days, unpaid',
  'for urgent matters, max. 16 hours or 2 days, 50% paid',
  'for managers only, 100% paid',
  '{{count}} of work included',
  '{{value}} VAT',
  'Reported work time',
  'h',
  'm',
  'out of {{time}}',
  '{{time}} left in {{month}}',
  'total in {{date}}',
  'this month',
  'Out of office',
  'Hours with a special rate. Report only with the consent of your manager.',
  'Time report preferences',
  'Calendar',
  'Work time insights',
  'Finances',
  'Breakdown of reported time',
  'There’s nothing here yet - the breakdown will appear once hours have been reported for this month.',
  '{{time}} overtime',
  '{{time}} missing as of today',
  '{{time}} missing',
  '{{time}} exceeding',
  'Flextime balance is correct',
  'Your flextime balance is correct as of today',
  'You can take these hours off by <strong>{{date}}</strong>',
  'Remember to make up for the missing hours by <strong>{{date}}</strong>',
  'Today\'s status',
  'remote work this month',
  'Hours used to calculate the remote work refund.',
  'No bonuses were awarded this month',
  'No Luminis products to show.',
  'Direct reporting',
  'Practice',
  'Operational',
  'Reporting for this period in this project is not open yet. Please try again once the next periods become available.',
  'Transfer',
  'Leave & training',
  'Limited leave {{year}}',
  'Leave {{year}}',
  'Unlimited leave {{year}}',
  'max. {{days}}',
  'max. {{days}} / {{hours}}',
  'max. {{days}} this year',
  '{{time}} used',
  '{{time}} left',
  'Training package {{year}}',
  'Training time',
  'max. {{amount}} this year',
  '{{amount}} used',
  '{{amount}} left',
  'max. {{amount}}',
  'Not assigned',
  'Direct reporting · line structure',
  '{{start}} - no end date',
  'Planned',
  'Projects',
  'No assigned projects',
  'Hide inactive entries',
  'No active entries',
  'This change results from changes to the assigned project.',
  'Hidden',
  "Progression doesn't apply to this contract",
  'Salary for {{date}} is no longer in the incomplete data state',
  'Project marked as primary by default.',
  'Direct subordinates',
  'Transfer deduction',
  'To be used by {{date}}',
  'Used',
  'Left',
  'Annual leave (hours)',
  'Annual leave (days)',
  '{{time}} taken from allowance',
  'No leave used in {{year}}',
  'Primary project',
  'Export CSV',
  'Export visible rows as CSV',
  'Table properties',
  'There was an error while downloading the file.',
  'There was an error while exporting CSV.',
  'Export CSV for this month',
  'Space owner',
  'Practice lead',
  'Operational lead',
  'their {{roles}}',
  'Hours would exceed the daily work limit ({{time}})',
  'Next month',
  'Previous month',
  'Pending leave approval',
  'Pending overtime approval',
  'Approved',
  'by {{name}}',
  'Space {{name}}',
  'Project {{name}}',
  '8 h standard comparison',
  'Working days x 8h',
  'Reported work time + absence time - expected work time',
  'Public holidays are not included',
  'Reported absence',
  'Ended',
  'Active',
  'Internal',
  'Space and position history',
  'Position',
  'Space',
  'Now',
  'Filter by',
  'Clear all',
  'Search',
  'Change filters or clear them all',
  'Clear all filters',
  'Person',
  'Entry for person: {{personName}}',
  '[Unauthorized]',
  'Copyrights',
  'Additional costs form',
  'File form',
  'File',
  'File actions',
  'Enter {{type}} number',
  'Upload {{type}}',
  'Work transfer',
  'Drawer with copyrights',
  'The work transfer is waiting for approval from a Financial Specialist. You can still replace the file with another version.',
  'The work transfer was checked by a Financial Specialist and marked as invalid. You cannot replace the file yourself. A Financial Specialist will contact you.',
  'The work transfer was checked by a Financial Specialist and marked as correct. You cannot replace the file.',
  'Missing work transfer number',
  'No work transfer for this month',
  'Missing work transfer file',
  'Add document',
  'Work transfers for {{period}}',
  'Current work transfers',
  'Copyrights for {{period}}',
  'document',
  'No works to be transferred this month',
  'invoice',
  'Discard unsaved changes?',
  'Discard',
  '{{year}}: {{amount}} used',
  'Go to training package guide',
  'Approved by {{person}}',
  'Valid from {{benefitValidDate}} · Variant valid from {{variantValidDate}}',
  'max. 35 months, unpaid',
  'max. 35 months in total',
  'For parents',
  'Beneficiaries',
  'Invoice to',
  'No more people specified in Friends & Family',
  'Amount in PLN (net)',
  'Amount in PLN (gross)',
  'Amount (net)',
  'Amount (gross)',
  'Amount paid',
  'Paid date',
  'Invoice to: {{person}}',
  '3rd party',
  'Unspecified 3rd party',
  'promisor',
  'promisee',
  'Buyer type',
  'Rate changes',
  'Progression',
  'Working time',
  'Basic rate',
  '(net)',
  '(gross)',
  'Change value',
  'percent',
  'day',
  'hour',
  'month',
  '{{name}} (net)',
  '{{name}} (gross)',
  'N/A',
  'Full-time',
  'Part-time',
  'New rates',
  'On-call turned off',
  'Rates',
  'Overtime turned off',
  'Non-engineering',
  'Engineering',
  'VL/Engineering/',
  'VL/Non-engineering/',
  'SML/Engineering/',
  'SML/Non-engineering/',
  'The employee hasn\'t had a rate change in the last {{number}} months.',
  'Progression level',
  'effective date',
  'Rate change',
  '(NE)',
  'Overtime rate',
  'Bank account actions',
  'On-call rates',
  'Last rate change',
  'Basic rate per {{settlement}}',
  'See salary range',
  'Rate change value',
  'This month',
  'Progression & type',
  'Uploaded by {{user}}',
  'Select specific days',
  'On {{dates}}',
  'Access restricted',
  'It looks like you don\'t have the required admin permissions to view this page.',
  'Need access? The NFS team is ready to help.',
  'Switch to the NFS user app',
  'No options match your query',
  'Skills',
  'Sectors',
  'Remove skill',
  'Remove sector',
  'Search skills',
  'Search sectors',
  "You can also search by category, e.g. '{{examples}}'.",
  'Search for {{element}}',
  'Select one or more {{type}} files',
  'File upload',
  'Files selected:',
  'Processing file {{current}} of {{total}}',
  'Leave on demand ({{daysLeft}})',
  'Leave on demand can only be reported for a single day',
  'For monitored accounts, NFS will fetch recent transfers, match them with sales invoices and send notifications.',
  '{{days}} left',
  'On demand used',
  'on demand',
  'You may take up to {{limit}} of annual leave on demand',
  'Third party',
  'Third party part of the package',
  'Invoice number',
  'KSeF number',
  'Value (gross)',
  'Saldeo',
  'File (PDF)',
  'Uploading a duplicate PDF is unnecessary and should be avoided.',
  'If you issue an invoice through the KSeF system, it should appear in the list below within one hour.',
  'Once an invoice has been sent via KSeF, you do not need to upload a PDF copy in this form.',
  'Announcement regarding KSeF (Krajowy System e-Faktur)',
  'Street address',
  'Postal code',
  'City or location',
  'Region',
  'Country',
  'Set a different mailing address',
  'Emergency contact',
  '(optional)',
  'Upload logo',
  'Delete logo',
  'Company logo',
  'Edit logo',
  'Logo',
  'Change file',
  'Since {{date}}',
  'Additional costs',
  'Drawer with additional costs',
  'Add an additional cost',
  'No additional costs for this month',
  'Value (net)',
  'Category',
  'Rejected',
  'Additional costs for {{date}}',
  'Description',
  'Yes',
  'No',
  'This employee has already been granted a language learning bonus ({{language}}) for the selected quarter.',
  'This employee has already been granted 2 language learning bonuses for the selected quarter.',
  'Only one language learning bonus per quarter and language can exist in a given year',
  'Quarter',
  'Remote work equipment',
  'Certificate',
  'Certificates',
  'Search for a certificate',
  'Issued: {{date}}',
  'Expires: {{date}}',
  'Click to open file',
  'Issue date',
  'Expiration date',
  'Exp. date',
  '{{name}} (opt.)',
  'Upload opt. attachment',
  'Skills, competences & certificates',
  'Edit skills, competences & certificates',
  'Skills & competences',
  'There is nothing here yet',
  'Edit certificate',
  'Delete certificate',
  'Certificate actions',
  'Add certificate',
  'I have no certificates to declare',
  'User declared they have no certificates',
  'Certificates declaration',
  'Save skills',
  'Expiration date must be after issue date',
  "If you notice a skill, technology, competence, certificate, or an entire category that's missing from the list — let us know at",
  'Complete your profile',
  'Please complete the following:',
  'Add at least {{count}} skills',
  'Declare your certificates or indicate you have none to declare',
  'Add your area of expertise',
  'For more context, please check the <slackLink>Slack announcement</slackLink>.',
  'Go to My Profile',
  'I understand',
  'Error: {{error}}, {{errorMessage}}',
  'You can either log out or try to reload the page. You may also let us know about it at',
  'An authentication error has occurred',
  'Unknown auth error',
  'No message',
  'Log out',
  'List entry for {{contractName}}',
  'Expand / hide {{contractName}}',
  'Social media handles',
  'Platform',
  'Handle',
  'Add social media handle',
  'A plate number must be 4 to 10 letters or digits',
  'Plate number',
  'Remove social media handle',
  'Król wód',
  'Level',
  'Failed to load {{thing}}',
  'Deselect all',
  'Add an attachment',
  'Attachment (optional)',
  'A file can only be attached when the form creates a single leave period',
  'Attachment',
  'Saving changes, please wait…',
  'Deleting invoice, please wait…',
  'Save the previous invoice to add another one',
  'You\'ve already used up your {{workTag}} this year',
  'The timesheet for this period is already blocked',
  'You can report entries up to {{date}}',
  'No active project assignment on this day',
  'No active on-call assignment on this day',
  'This file type cannot be opened for security reasons.',
  '{{projectName}} period: {{from}}/{{to}}',
  '{{projectName}} period: {{from}}',
  'Prev',
  'Next',
  'Until the end of {{projectName}}\'s period ({{date}})',
  'Time report',
  'Account settings',
  'No contract',
  'Team absences',
  'History',
  'Basic info',
  'Work e-mail',
  'Name',
  'Office',
  'Area of expertise',
  'Authorized to process personal data',
  'Consent for use of image',
  'Student under 26 years old',
  '(to: {{date}})',
  'Additional data',
  'Identity document',
  'PESEL',
  'Citizenship',
  'Birth date',
  'Next medical examination',
  'Next OHS training date',
  'Passport expiration date',
  'Tax office',
  'Address & contact details',
  'Private e-mail',
  'Private phone number',
  'Residence address',
  'Mailing address',
  'Paczkomat',
  'Edit additional data',
  'Edit address & contact details',
  'Edit profile details',
  'Edit social media handles',
  'Drawer with a form for editing address and contact details',
  'Profile',
  'Contact details',
  'Mailing & delivery',
  '{{name}} (optional)',
  'Hide details for {{contractName}}',
  'Show details for {{contractName}}',
  'Terminated on {{date}}',
  '{{contractType}} Contract',
  'Subcontract',
  'Added to Intra-group or external subcontract on {{date}}',
  'Concluded on {{date}}',
  'Valid from {{startDate}} to <box><alert>{{endDate}}</alert></box>',
  'Valid from {{date}} ',
  'Additional costs added by an admin will appear here',
  'Already taken on {{dates}}',
  'No parking spot is free on {{dates}}. Try other dates.',
  'Desks can be booked up to {{date}}',
  'Past dates cannot be booked',
  'Monitors: {{monitors}}',
  'Standing desk',
  'Taken on {{dates}}',
  'A desk can be booked for at most {{days}} days at once',
  'Parking can be booked for at most {{days}} days at once',
  'Room',
  'Desks',
  'Remove desk',
  'Choose a room first',
  'Notes',
  'Parking can be booked up to {{date}}',
  'Preferred spot',
  'Guest spot',
  'Any available spot',
  'Book {{name}} on {{date}}',
  'Desk or parking spot',
  'Expand {{name}}',
  'Collapse {{name}}',
  '{{booked}} of {{total}} booked',
  'Legend:',
  'Request booking',
  'Book',
  'Auto-approved',
  'This booking will be confirmed straight away, without waiting for approval.',
  'Dates',
  'Guest e-mails',
  'Remove guest',
  'Email address',
  '{{office}} opens on {{date}}',
  '{{office}} is open until {{date}}',
  'Floor map',
  'Parking map',
  'No map has been uploaded for this office yet',
  'Parking',
  'Spots availability & booking',
  'Book parking',
  'Book a desk',
  'This office has no desks or parking spots to book',
  'Booking',
  'Today and the remaining days will be released for others. Past days stay booked.',
  'The desk or parking spot will be released for others.',
  'Keep booking',
  'Cancel booking',
  'Parking booking',
  'Used for parking bookings',
  'Desk booking',
] as const;

export default sharedMessages;
